import React from 'react';
import { Box, Switch, Typography } from '@mui/material';
import { useAppTranslation } from '../i18n/useAppTranslation';

export type RebateDiscountKind = 'instant' | 'bundle';

export interface RebateDiscountChoiceProps {
  kind: RebateDiscountKind;
  /** On unless the store turned it off (a missing setting is on). */
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** The `write_discounts` scope is missing: the switch cannot do anything until the store reconnects. */
  disabled?: boolean;
  /** Instant rebates only reach checkout as discounts under the "separate discount" treatment. */
  treatmentNote?: string | null;
}

const WHY_OFF_KEYS = ['off1', 'off2', 'off3', 'off4'] as const;

/**
 * One store-level switch for a kind of Shopify rebate discount, with the plain
 * account of what on does and the reasons a store might want it off. Used by the
 * Shopify rebate-discounts settings and by the setup walkthrough.
 */
const RebateDiscountChoice: React.FC<RebateDiscountChoiceProps> = ({
  kind,
  checked,
  onChange,
  disabled = false,
  treatmentNote,
}) => {
  const { t } = useAppTranslation();
  const prefix = `integrations.rebateDiscounts.choice.${kind}`;
  const label = t(`${prefix}.title`);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, mr: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
            {t(`${prefix}.on`)}
          </Typography>
        </Box>
        <Switch
          size="small"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          inputProps={{ 'aria-label': label }}
        />
      </Box>
      {treatmentNote && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', lineHeight: 1.45 }}>
          {treatmentNote}
        </Typography>
      )}
      <Box>
        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
          {t('integrations.rebateDiscounts.choice.whyOffTitle')}
        </Typography>
        <Box component="ul" sx={{ m: 0, mt: 0.25, pl: 2.5 }}>
          {WHY_OFF_KEYS.map((key) => (
            <Typography key={key} component="li" variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {t(`${prefix}.${key}`)}
            </Typography>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.45 }}>
          {t('integrations.rebateDiscounts.choice.offNote')}
        </Typography>
      </Box>
    </Box>
  );
};

export default RebateDiscountChoice;
